import { useGeoBasedLocale } from './useGeoLocale';

/**
 * Supported languages for the Travel White Label.
 * These correspond to language codes used in the LiteAPI deeplinking documentation:
 * https://docs.liteapi.travel/docs/deeplinking-to-whitelabel#language
 */
export enum NuiteeLanguage {
  en = 'en',
  fr = 'fr',
  ru = 'ru',
  it = 'it',
  nl = 'nl',
  es = 'es',
  tr = 'tr',
  de = 'de',
  ar = 'ar',
  pt = 'pt',
  el = 'el',
  ro = 'ro'
}

/**
 * Hook that returns the appropriate NuiteeLanguage based on the user's geolocation.
 * If the language cannot be determined or is still loading, it defaults to English (`en`).
 *
 * @returns {[NuiteeLanguage, { loading: boolean }]}
 *   - The first value is a valid NuiteeLanguage enum value.
 *   - The second value is an object indicating whether the language is still being loaded.
 */
export const useNuiteeLanguage = (): [NuiteeLanguage, { loading: boolean }] => {
  const { loading, language: localeLanguage } = useGeoBasedLocale();

  if (loading) {
    // Default to English while loading
    return [NuiteeLanguage.en, { loading }];
  }

  const language = localeLanguage as keyof typeof NuiteeLanguage;
  const validLanguage = NuiteeLanguage[language] || NuiteeLanguage.en;

  return [validLanguage, { loading }];
};
